/**
 * High-variant product support for products with more than 250 variants.
 *
 * Shopify caps unpaginated product.variants Liquid output at 250. This
 * controller uses product_option_value IDs and the Section Rendering API so
 * the existing product templates can select variants beyond that limit.
 */
(() => {
  const PICKER_SELECTOR = [
    'variant-selects[data-high-variant="true"]',
    'secondary-variant-select[data-high-variant="true"]',
    'secondary-variant-select-separate[data-high-variant="true"]',
  ].join(',');

  const OPTION_CONTROL_SELECTOR = '[data-option-value-id]';
  const requests = new Map();

  const selectedControl = (group) =>
    group.querySelector('input[type="radio"]:checked') ||
    group.querySelector('select option:checked');

  const getSelectedOptionValueIds = (picker) =>
    Array.from(picker.querySelectorAll('.product-form__input'))
      .map(selectedControl)
      .filter(Boolean)
      .map((control) => control.dataset.optionValueId)
      .filter(Boolean);

  const parseSelectedVariant = (root) => {
    const json = root?.querySelector('script[data-selected-variant]')?.textContent;
    if (!json) return null;

    try {
      return JSON.parse(json);
    } catch (error) {
      console.error('Unable to parse the selected high-variant product variant.', error);
      return null;
    }
  };

  const getSelectedTarget = (target) =>
    target.matches('select') ? target.selectedOptions?.[0] : target;

  const getProductUrl = (picker, target) =>
    getSelectedTarget(target)?.dataset.productUrl ||
    picker.dataset.url ||
    window.location.pathname;

  const buildRequestUrl = (productUrl, sectionId, optionValueIds) => {
    const url = new URL(productUrl, window.location.origin);
    url.searchParams.set('section_id', sectionId);
    url.searchParams.set('option_values', optionValueIds.join(','));
    url.searchParams.delete('variant');
    return url;
  };

  const shouldPreserveControl = (control) => {
    if (!control.name || control.matches(OPTION_CONTROL_SELECTOR)) return false;
    if (control.name === 'id' || control.name.startsWith('options[')) return false;
    if (control.type === 'file' || control.type === 'submit' || control.type === 'button') return false;
    return true;
  };

  const snapshotUiState = (container) => {
    const occurrence = new Map();
    const controls = [];

    container.querySelectorAll('input[name], textarea[name], select[name]').forEach((control) => {
      if (!shouldPreserveControl(control)) return;

      const type = control.type || control.tagName.toLowerCase();
      const key = `${control.tagName}|${type}|${control.name}`;
      const index = occurrence.get(key) || 0;
      occurrence.set(key, index + 1);

      controls.push({
        key,
        index,
        tagName: control.tagName,
        type,
        name: control.name,
        value: control.value,
        checked: Boolean(control.checked),
        selectedValues:
          control instanceof HTMLSelectElement && control.multiple
            ? Array.from(control.selectedOptions).map((option) => option.value)
            : null,
      });
    });

    const openDetails = Array.from(container.querySelectorAll('details[open][id]')).map(
      (details) => details.id
    );

    return { controls, openDetails };
  };

  const restoreUiState = (container, state) => {
    if (!state) return;

    const grouped = new Map();
    container.querySelectorAll('input[name], textarea[name], select[name]').forEach((control) => {
      if (!shouldPreserveControl(control)) return;
      const type = control.type || control.tagName.toLowerCase();
      const key = `${control.tagName}|${type}|${control.name}`;
      if (!grouped.has(key)) grouped.set(key, []);
      grouped.get(key).push(control);
    });

    state.controls.forEach((saved) => {
      const control = grouped.get(saved.key)?.[saved.index];
      if (!control) return;

      if (control instanceof HTMLSelectElement && control.multiple && saved.selectedValues) {
        Array.from(control.options).forEach((option) => {
          option.selected = saved.selectedValues.includes(option.value);
        });
      } else if (control.type === 'checkbox' || control.type === 'radio') {
        control.checked = saved.checked;
      } else {
        control.value = saved.value;
      }
    });

    state.openDetails.forEach((id) => {
      const details = container.querySelector(`#${CSS.escape(id)}`);
      if (details) details.open = true;
    });
  };

  const publishVariantChange = (sectionId, html, variant) => {
    const detail = { sectionId, html, variant };

    document.dispatchEvent(
      new CustomEvent('variant:change', {
        bubbles: true,
        detail,
      })
    );

    try {
      if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS?.variantChange) {
        window.publish(window.PUB_SUB_EVENTS.variantChange, { data: detail });
      } else if (
        typeof publish === 'function' &&
        typeof PUB_SUB_EVENTS !== 'undefined' &&
        PUB_SUB_EVENTS.variantChange
      ) {
        publish(PUB_SUB_EVENTS.variantChange, { data: detail });
      }
    } catch (error) {
      // The theme's internal pub/sub API is optional here.
    }
  };

  const sanitizeQuickAddSection = (sectionRoot) => {
    const clone = sectionRoot.cloneNode(true);
    clone
      .querySelectorAll('pickup-availability, product-modal, sticky-atc, modal-dialog')
      .forEach((element) => element.remove());
    return clone;
  };

  const prepareQuickAddPickers = (container, renderSectionId) => {
    container.querySelectorAll('variant-selects, variant-radios, product-info').forEach((element) => {
      element.dataset.originalSection = renderSectionId;
    });

    container
      .querySelectorAll('variant-selects, variant-radios')
      .forEach((element) => element.setAttribute('data-update-url', 'false'));
  };

  const replaceRenderedSection = ({
    picker,
    html,
    domSectionId,
    renderSectionId,
    currentSection,
    quickAddContainer,
  }) => {
    if (quickAddContainer) {
      const incomingMainProduct =
        html.querySelector(`#MainProduct-${CSS.escape(renderSectionId)}`) ||
        html.querySelector('section[id^="MainProduct-"]');

      if (!incomingMainProduct) {
        throw new Error('The refreshed quick-add product section could not be found.');
      }

      const sanitized = sanitizeQuickAddSection(incomingMainProduct);
      quickAddContainer.innerHTML = sanitized.innerHTML.replaceAll(renderSectionId, domSectionId);
      prepareQuickAddPickers(quickAddContainer, renderSectionId);
      return quickAddContainer;
    }

    if (currentSection) {
      const incomingWrapper = html.getElementById(`shopify-section-${renderSectionId}`);
      const renderedMarkup = (incomingWrapper?.innerHTML || html.body.innerHTML).trim();
      if (!renderedMarkup) {
        throw new Error('The refreshed product section was empty.');
      }
      currentSection.innerHTML = renderedMarkup;
      return currentSection;
    }

    const currentProductInfo = picker.closest('product-info');
    const incomingProductInfo = html.querySelector('product-info');
    if (currentProductInfo && incomingProductInfo) {
      currentProductInfo.innerHTML = incomingProductInfo.innerHTML;
      currentProductInfo.dataset.url = incomingProductInfo.dataset.url || currentProductInfo.dataset.url;
      return currentProductInfo;
    }

    throw new Error('The current product section could not be refreshed.');
  };

  const refreshSection = async (picker, target) => {
    const domSectionId = picker.dataset.section;
    const renderSectionId = picker.dataset.originalSection || domSectionId;
    const optionValueIds = getSelectedOptionValueIds(picker);

    if (!domSectionId || !renderSectionId || optionValueIds.length === 0) return;

    const quickAddContainer = picker.closest('[id^="QuickAddInfo-"]');
    const currentSection = quickAddContainer
      ? null
      : document.getElementById(`shopify-section-${domSectionId}`) || picker.closest('.shopify-section');
    const currentContainer = quickAddContainer || currentSection || picker.closest('product-info');

    if (!currentContainer) return;

    const requestKey = `${domSectionId}:${quickAddContainer ? 'quick-add' : 'section'}`;
    requests.get(requestKey)?.abort();
    const controller = new AbortController();
    requests.set(requestKey, controller);

    const selectedTarget = getSelectedTarget(target);
    const changedOptionValueId = selectedTarget?.dataset.optionValueId || '';
    const changedPickerId = picker.id || '';
    const updateUrl = picker.dataset.updateUrl !== 'false';
    const productUrl = getProductUrl(picker, target);
    const requestUrl = buildRequestUrl(productUrl, renderSectionId, optionValueIds);
    const uiState = snapshotUiState(currentContainer);

    currentContainer.setAttribute('aria-busy', 'true');
    currentContainer.classList.add('high-variant-loading');

    try {
      const response = await fetch(requestUrl.toString(), {
        signal: controller.signal,
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });

      if (!response.ok) {
        throw new Error(`High-variant section request failed (${response.status}).`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      const variant = parseSelectedVariant(html);
      const refreshedContainer = replaceRenderedSection({
        picker,
        html,
        domSectionId,
        renderSectionId,
        currentSection,
        quickAddContainer,
      });

      restoreUiState(refreshedContainer, uiState);
      refreshedContainer.removeAttribute('aria-busy');
      refreshedContainer.classList.remove('high-variant-loading');

      window.Shopify?.PaymentButton?.init?.();
      window.ProductModel?.loadShopifyXR?.();

      const quickAddModal = quickAddContainer?.closest('quick-add-modal');
      quickAddModal?.removeGalleryListSemantic?.();
      quickAddModal?.updateImageSizes?.();

      if (updateUrl) {
        const cleanProductUrl = new URL(productUrl, window.location.origin);
        cleanProductUrl.search = '';
        if (variant?.id) cleanProductUrl.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', `${cleanProductUrl.pathname}${cleanProductUrl.search}`);
      }

      if (window.ntc?.productData && variant) {
        window.ntc.productData.variantId = variant.id;
        window.ntc.productData.price = variant.price;
      }

      publishVariantChange(renderSectionId, html, variant);

      if (changedOptionValueId) {
        requestAnimationFrame(() => {
          const refreshedPicker = changedPickerId
            ? refreshedContainer.querySelector(`#${CSS.escape(changedPickerId)}`)
            : null;
          (refreshedPicker || refreshedContainer)
            .querySelector(`[data-option-value-id="${CSS.escape(changedOptionValueId)}"]`)
            ?.focus?.();
        });
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error(error);
        currentContainer.removeAttribute('aria-busy');
        currentContainer.classList.remove('high-variant-loading');
      }
    } finally {
      if (requests.get(requestKey) === controller) requests.delete(requestKey);
    }
  };

  document.addEventListener(
    'change',
    (event) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;

      const picker = target.closest(PICKER_SELECTOR);
      if (!picker) return;

      // Stop the legacy variant controller, which relies on the first 250 variants.
      event.stopImmediatePropagation();
      refreshSection(picker, target);
    },
    true
  );
})();
